import React, { useState, useEffect } from "react";
import Typography from "aldoo-ra/Typography";
import Input from "aldoo-ra/Input";
import Checkbox from "aldoo-ra/Checkbox";
import Button from "aldoo-ra/Button";

/**
 * FieldEditor Component
 * Renders appropriate input based on field type
 */
const FieldEditor = ({
  fieldDefinition,
  fieldType,
  value,
  onChange,
  label,
  error,
  variant = "admin"
}) => {
  const type = fieldDefinition?.type || fieldType;
  const [isValueSet, setIsValueSet] = useState(value !== undefined);

  // Reset isValueSet when value changes to undefined
  useEffect(() => {
    if (value === undefined) {
      setIsValueSet(false);
    }
  }, [value]);

  if (!type) {
    return (
      <Typography variant="admin_caption" className="text-admin_muted">
        Select a field first
      </Typography>
    );
  }

  // Helper to get default value for field type
  const getDefaultValue = (fieldType) => {
    switch (fieldType) {
      case 'boolean':
        return false;
      case 'number':
        return 0;
      case 'text':
      case 'longtext':
        return '';
      case 'date':
        return new Date();
      case 'json':
        return {};
      case 'select':
        return fieldDefinition?.options?.[0]?.value || '';
      default:
        return '';
    }
  };

  // Handle "Set Value" button click
  const handleSetValue = () => {
    const defaultValue = getDefaultValue(type);
    onChange(defaultValue);
    setIsValueSet(true);
  };

  // If value is undefined, show "Set Value" button
  if (!isValueSet || value === undefined) {
    return (
      <div>
        {label && (
          <Typography variant="admin_label" className="mb-1">
            {label}
          </Typography>
        )}
        <Button
          variant="admin_secondary"
          onClick={handleSetValue}
          className="w-full"
        >
          Set Value
        </Button>
      </div>
    );
  }

  switch (type) {
    case 'text':
      return (
        <div>
          {label && (
            <Typography variant="admin_label" className="mb-1">
              {label}
            </Typography>
          )}
          <Input
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            variant={variant}
            className="w-full"
          />
          {error && (
            <Typography variant="admin_caption" className="text-admin_error mt-1">
              {error}
            </Typography>
          )}
        </div>
      );

    case 'longtext':
      return (
        <div>
          {label && (
            <Typography variant="admin_label" className="mb-1">
              {label}
            </Typography>
          )}
          <textarea
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            className={`
              w-full min-h-[120px] p-2 rounded
              border border-admin_bordered
              bg-admin_surface text-admin_text
              focus:outline-none focus:ring-2 focus:ring-admin_primary
            `}
          />
          {error && (
            <Typography variant="admin_caption" className="text-admin_error mt-1">
              {error}
            </Typography>
          )}
        </div>
      );

    case 'number':
      return (
        <div>
          {label && (
            <Typography variant="admin_label" className="mb-1">
              {label}
            </Typography>
          )}
          <Input
            type="number"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
            variant={variant}
            className="w-full"
          />
          {error && (
            <Typography variant="admin_caption" className="text-admin_error mt-1">
              {error}
            </Typography>
          )}
        </div>
      );

    case 'boolean':
      return (
        <div>
          {label && (
            <Typography variant="admin_label" className="mb-1">
              {label}
            </Typography>
          )}
          <div className="flex items-center">
            <Checkbox
              isOn={!!value}
              onChange={onChange}
              variant="admin"
              label={value ? 'true' : 'false'}
            />
          </div>
          {error && (
            <Typography variant="admin_caption" className="text-admin_error mt-1">
              {error}
            </Typography>
          )}
        </div>
      );

    case 'date':
      return (
        <div>
          {label && (
            <Typography variant="admin_label" className="mb-1">
              {label}
            </Typography>
          )}
          <Input
            type="date"
            value={value ? new Date(value).toISOString().split('T')[0] : ''}
            onChange={(e) => onChange(e.target.value ? new Date(e.target.value) : null)}
            variant={variant}
            className="w-full"
          />
          {error && (
            <Typography variant="admin_caption" className="text-admin_error mt-1">
              {error}
            </Typography>
          )}
        </div>
      );

    case 'select':
      return (
        <div>
          {label && (
            <Typography variant="admin_label" className="mb-1">
              {label}
            </Typography>
          )}
          <select
            className="w-full p-2 border border-admin_bordered rounded bg-admin_surface text-admin_text"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
          >
            <option value="">Select option</option>
            {fieldDefinition?.options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {error && (
            <Typography variant="admin_caption" className="text-admin_error mt-1">
              {error}
            </Typography>
          )}
        </div>
      );

    case 'json':
      return (
        <div>
          {label && (
            <Typography variant="admin_label" className="mb-1">
              {label}
            </Typography>
          )}
          <textarea
            value={typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
            onChange={(e) => {
              try {
                onChange(JSON.parse(e.target.value));
              } catch {
                onChange(e.target.value);
              }
            }}
            className={`
              w-full min-h-[120px] p-2 rounded font-mono text-sm
              border border-admin_bordered
              bg-admin_surface text-admin_text
              focus:outline-none focus:ring-2 focus:ring-admin_primary
            `}
            placeholder="{}"
          />
          {error && (
            <Typography variant="admin_caption" className="text-admin_error mt-1">
              {error}
            </Typography>
          )}
        </div>
      );

    default:
      return (
        <div>
          {label && (
            <Typography variant="admin_label" className="mb-1">
              {label}
            </Typography>
          )}
          <Input
            value={typeof value === 'string' ? value : JSON.stringify(value)}
            onChange={(e) => onChange(e.target.value)}
            variant={variant}
            className="w-full"
          />
          {error && (
            <Typography variant="admin_caption" className="text-admin_error mt-1">
              {error}
            </Typography>
          )}
          <Typography variant="admin_caption" className="text-admin_muted mt-1">
            Editing {type} field
          </Typography>
        </div>
      );
  }
};

export default FieldEditor;
